/**
 * useCartProducts -- extracted from CartProducts.vue setup()
 *
 * @param {Object} props  - component props (cartProducts, isPopup, retailerId, modalId)
 * @param {Function} emit - component emit function
 * @param {Object} root   - Vue root instance (from setup's ctx.root)
 */
import analytics from '@/@core/utils/analytics'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import {
  formatNumber,
  formatObject,
  getMaxQuantity,
  hasProductMaxQuantity,
  imageLoadError,
  isProductValueDisabled,
  searchProductUsingFields,
} from '@/@core/utils/utils'
import constants, {
  CART_PRODUCTS_SEARCH_FIELDS,
  CART_PRODUCTS_SEARCH_ITEMS_FIELDS,
  PRODUCTS_LIMIT,
} from '@/constants'
import {
  buildCartSearchIndex,
  CART_SEARCH_INDEX_THRESHOLD,
} from '@/@core/utils/cart-search-index'
import store from '@/store'
import {
  FETCH_CART_COUNT,
  REMOVE_PRODUCT_FROM_CART,
  SET_REPLACE_CART_PRODUCT,
  UPDATE_CART_ITEMS_COUNT,
} from '@/store/modules/shop.module'
import { LOAD_CARTS, UPDATE_CART_ERRORS } from '@/store/modules/checkout-v2.module'
import {
  utils,
  productUtil,
} from '@kingpin-global/kingpin-utils-frontend'
import { onMounted, onUnmounted, watch } from '@vue/composition-api'
import {
  seedAndSnapshotCartProducts,
  initializeData,
  isNotValidMinQty,
  getCartProductsErrorStatusText,
  isProductHasZeroQty,
  isProductWithDecimalQty,
  minQtyErrorMessage,
  parseProductItems,
  processProductVariants,
  getSortedCartProducts,
  isSegmentQuantityBlocked,
  isSegmentCannotOrder,
  segmentMaxQtyMessage,
} from '@/views/apps/checkout/cart-products-utils'
import { useEcommerce, useEcommerceUi } from '@/views/apps/useEcommerce'
import { useCartProductTrackers } from '@/views/apps/checkout/trackHelpers/useCartProductsTrackers'

const { getProductAndCartTotals } = productUtil
const { formatCurrency, formattedDate, round } = utils

// ---------------------------------------------------------------------------
// Module-level pure helpers (no closure dependencies)
// ---------------------------------------------------------------------------

/**
 * Checks whether a single variant row has any size whose quantity
 * falls below the product's minQtyPerSize threshold.
 */
function hasMinQtySizeViolation(variantRow, minQtyPerSize) {
  return Object.keys(variantRow)
    .filter(k => k !== 'color')
    .some(k => variantRow[k].value && variantRow[k].value < minQtyPerSize)
}

/**
 * Checks all variant rows for a single product and pushes a per-size
 * validation message when a violation is found.
 */
function collectMinQtyPerSizeErrors(prod, messages) {
  prod.variants.forEach(variantRow => {
    if (hasMinQtySizeViolation(variantRow, prod.minQtyPerSize)) {
      messages.push(
        `Minimum quantity per size for "${prod.name}" is ${prod.minQtyPerSize}`,
      )
    }
  })
}

/** KP-4605: the segment maximum, checked on top of the product's own minimums. */
function collectSegmentMaxQtyError(prod, messages) {
  if (isSegmentQuantityBlocked(prod, prod.lineTotalQuantity)) {
    messages.push(segmentMaxQtyMessage(prod))
  }
}

/** The product's own per-size and per-style minimums. */
function collectProductMinQtyErrors(prod, messages) {
  if (!prod?.isCarton && prod?.minQtyPerSize) {
    collectMinQtyPerSizeErrors(prod, messages)
  }
  if (prod?.minQtyPerStyle && prod.lineTotalQuantity < prod.minQtyPerStyle) {
    messages.push(
      `Minimum quantity per style for "${prod.name}" is ${prod.minQtyPerStyle}`,
    )
  }
}

/**
 * Collects all minimum-quantity validation error messages for the given products.
 * Returns an array of human-readable error strings (empty if all products pass).
 */
export function collectMinQtyValidationErrors(productsList) {
  const messages = []

  productsList.forEach(prod => {
    // KP-4605: a style whose minimum is above its segment maximum gets one message
    if (isSegmentCannotOrder(prod)) {
      messages.push(segmentMaxQtyMessage(prod))
      return
    }
    collectProductMinQtyErrors(prod, messages)
    // KP-4605: the segment maximum is an extra limit
    collectSegmentMaxQtyError(prod, messages)
  })

  return messages
}

/**
 * Builds a single payload entry for one product when saving the cart.
 * Returns an object to push into the payload, or null if no update is needed.
 * Returns the string 'USE_VARIANT_PROCESSOR' when variant processing is needed.
 */
function buildProductPayloadEntry(prod, originalProduct, isBrandRef) {
  if (prod.isCarton) {
    const hasQuantityChanged = originalProduct?.quantity !== prod.quantity
    return (hasQuantityChanged || isBrandRef.value)
      ? { productId: prod._id, quantity: prod.quantity }
      : null
  }

  if (Array.isArray(prod.variants) && prod.variants.length > 0) {
    return 'USE_VARIANT_PROCESSOR'
  }

  return {
    productId: prod._id,
    items: prod.items.map(item => ({
      sku: item.sku,
      value: item.value,
    })),
  }
}

/**
 * Determines whether the parsed items array has actually changed compared to
 * the previous items. Prevents unnecessary reassignment that triggers infinite
 * watcher loops in ProductVariantsMixin.
 */
function haveItemsChanged(newItems, prevItems) {
  if (newItems === prevItems) return false
  if (!prevItems) return true
  if (prevItems.length !== newItems.length) return true
  return newItems.some((item, idx) => item !== prevItems[idx])
}

/**
 * Computes the line-level RRP for a single product (mutates product.lineRRP).
 */
function calculateLineRRP(product) {
  product.lineRRP = 0
  product.items.forEach(item => {
    product.lineRRP += product.retailPrice * (product.quantity || 1) * item.value
  })
  return product.lineRRP
}

/**
 * Computes the total RRP across all products.
 */
function computeTotalRRP(productsList) {
  let totalRRP = 0
  productsList.forEach(product => {
    totalRRP += calculateLineRRP(product)
  })
  return round(totalRRP) || 0
}

/**
 * Returns the upper slice bound capped at the total number of products.
 */
function computeSliceEnd(totalLength, limit) {
  return Math.min(totalLength, limit)
}

// ---------------------------------------------------------------------------
// Scroll helpers (pure -- only depend on arguments)
// ---------------------------------------------------------------------------

function getScrollContainer(modalId) {
  const bodyEl = document.getElementById(modalId)
  if (!bodyEl) return window
  // V2Modal renders the body div inside a <dialog class="v2-modal-overlay">
  // element. The dialog itself is the scroll container (it has
  // `overflow-y: auto`), so the infinite-scroll listener must attach there
  // — attaching to the body div would never fire because the body doesn't
  // scroll, and falling back to `window` (the previous behavior with the
  // hard-coded `.modal-body` lookup that doesn't match V2Modal's class)
  // also never fires because the page itself doesn't scroll while the
  // modal is open.
  const v2Dialog = bodyEl.closest('.v2-modal-overlay')
  if (v2Dialog) return v2Dialog
  // Legacy fallback — Bootstrap b-modal (used by non-v2 cart pages) wraps
  // the cart in a `.modal-body` element nested inside the modalId root.
  return bodyEl.querySelector('.modal-body') || window
}

function getScrollPosition(container) {
  return container === window ? window.scrollY : container.scrollTop
}

function getScrollHeight(container) {
  return container === window ? document.body.scrollHeight : container.scrollHeight
}

function getClientHeight(container) {
  return container === window ? window.innerHeight : container.clientHeight
}

function isNearBottom(container, threshold) {
  const position = getScrollPosition(container)
  const height = getScrollHeight(container)
  const client = getClientHeight(container)
  return position + client >= height - threshold
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useCartProducts(props, emit, root) {
  let productsLimit = PRODUCTS_LIMIT
  let productsSliceX = 0
  let productsSliceY = 0
  let pageNumber = 1
  let totals = { totalPrice: 0, totalQuantity: 0, totalPriceWithDiscount: 0 }
  let isCartModified = false
  // Lowercased searchable-token strings keyed by product._id, only populated
  // once the cart crosses CART_SEARCH_INDEX_THRESHOLD. Stays `null` for small
  // carts so they keep using the original field-by-field matcher.
  let searchIndex = null

  const {
    variantLoaded, isLoaded, products, productsSlice, searchText,
    originalProducts, rawProducts, collectionEndDate, discountInCredit,
    collectionName, collectionId, isBrand, isCartExport,
  } = initializeData(root, props)

  const { addProductInCart, REMOVE_PRODUCT_FROM_CART_MSG } = useEcommerce()
  const { parseProductImage } = useEcommerceUi()

  const {
    debouncedTrackSearchProductsInput,
    trackErrorPopupClicks,
    trackRemoveProduct,
  } = useCartProductTrackers({
    isBrand,
    collectionId,
    props,
    products,
    cartId: root.$store.getters.getCart._id,
    userId: root.$store.getters.getCart.userId,
  })

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  function trackBrandProductUpdate(product) {
    if (!isBrand.value) return
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_UPDATES_PRODUCT_IN_CART,
      formatObject(product),
    )
  }

  function loadCartIfEmpty() {
    if (products.value?.length || isBrand.value) return
    root.$store.dispatch(LOAD_CARTS)
  }

  function removeProductFromProductList(product) {
    const filterOutProduct = prod => prod._id !== product._id
    products.value = products.value.filter(filterOutProduct)
    originalProducts.value = originalProducts.value.filter(filterOutProduct)
    productsSlice.value = productsSlice.value.filter(filterOutProduct)
    if (searchIndex && product?._id) {
      delete searchIndex[product._id]
    }
    loadCartIfEmpty()
  }

  function refreshSlice() {
    productsSlice.value = []
    products.value.slice(0, productsSliceY).forEach(p => {
      productsSlice.value.push(p)
    })
  }

  function updateTotalPriceAndUnits() {
    const currency = products.value[0].currency
    totals = getProductAndCartTotals(products.value, products.value[0].discount)
    const totalRRP = computeTotalRRP(products.value)
    const totalMRP = totals.totalPriceWithDiscount || totals.totalPrice || 0

    if (props.isPopup) {
      refreshSlice()
    }

    // getProductAndCartTotals mutates lineTotalPrice/lineTotalQuantity on product
    // objects. In Vue 2 these properties aren't reactive (added after observation).
    // Reassigning the array refs triggers v-for / DynamicScroller re-renders
    // that pick up the new values. We reassign BOTH because:
    //   - V2CartProducts (virtualised) iterates `displayedProducts` derived
    //     from `products` — reassigning productsSlice alone wouldn't update it,
    //     so per-card Total Value / Total Units would stay stale after edits.
    //   - Legacy CartProducts (and any non-virtualised consumer) still v-for
    //     over `productsSlice`, so we keep that trigger too.
    products.value = products.value.slice()
    productsSlice.value = productsSlice.value.slice()

    emit('update-total', { totalMRP, totalRRP, currency })
    emit('update-total-units', totals.totalQuantity)
  }

  function appendSlicePage(fromIndex, toIndex) {
    products.value
      .slice(fromIndex, toIndex)
      .forEach(p => productsSlice.value.push(p))
  }

  // ---------------------------------------------------------------------------
  // Cart removal
  // ---------------------------------------------------------------------------

  async function removeProductFromCart(product) {
    try {
      await store.dispatch(REMOVE_PRODUCT_FROM_CART, { productId: product._id })
      trackRemoveProduct(product._id, 'product_remove_success')
      apiToastSuccess(REMOVE_PRODUCT_FROM_CART_MSG)
      store.commit(UPDATE_CART_ITEMS_COUNT, store.state.shop.cartItemsCount - 1)
      await store.dispatch(FETCH_CART_COUNT)
      removeProductFromProductList(product)

      if (products.value.length === 0) {
        emit('has-cart-product', false)
      } else {
        updateTotalPriceAndUnits()
      }

      analytics.track(constants.TRACKS.ACTIONS.REMOVE_FROM_CART, formatObject(product))
      store.commit(UPDATE_CART_ERRORS)
    } catch (err) {
      trackRemoveProduct(product._id, 'product_remove_failed', err)
    }
  }

  function removeProduct(product) {
    if (!props.isPopup) {
      removeProductFromCart(product)
      return
    }

    emit('remove-product-selected-from-brand', product)
    removeProductFromProductList(product)
    updateTotalPriceAndUnits()
    apiToastSuccess('Product removed successfully')
  }

  function removeMultipleProducts(removableBrandCartProductIds) {
    removableBrandCartProductIds.forEach(productId => {
      const product = products.value.find(p => p._id === productId)
      if (product) {
        removeProductFromProductList(product)
      }
    })

    if (products.value.length > 0) {
      updateTotalPriceAndUnits()
    }

    emit('remove-multiple-products', removableBrandCartProductIds)
    apiToastSuccess('Products removed successfully')
  }

  // ---------------------------------------------------------------------------
  // Replace / update actions
  // ---------------------------------------------------------------------------

  function isV2Context() {
    return root.$route?.meta?.layout === 'v2'
  }

  async function onClickReplace(product) {
    root.$store.commit(SET_REPLACE_CART_PRODUCT, product)
    const routeName = isV2Context() ? 'v2-collection/products' : 'collection/products'
    await root.$router.push({
      name: routeName,
      params: { collectionId: product.collectionId },
    })
  }

  function updateProductVariants(variants, product) {
    trackBrandProductUpdate(product)

    isCartModified = true
    product.variants = variants
    const items = parseProductItems(product)

    if (haveItemsChanged(items, product.items)) {
      product.items = items
    }

    const index = originalProducts.value.findIndex(p => p._id === product._id)
    originalProducts.value[index].items = items

    if (!product.isCarton) {
      originalProducts.value[index].hasError = product?.hasError || false
    }

    updateTotalPriceAndUnits()
    variantLoaded.value = true
  }

  function updateQuantity(product) {
    trackBrandProductUpdate(product)

    isCartModified = true
    const index = originalProducts.value.findIndex(p => p._id === product._id)
    originalProducts.value[index].quantity = product.quantity
    updateTotalPriceAndUnits()
  }

  // ---------------------------------------------------------------------------
  // fetchCartProducts -- broken into focused helpers
  // ---------------------------------------------------------------------------

  function populateProductsFromCart(cartProductsList) {
    const { original, raw } = seedAndSnapshotCartProducts(cartProductsList)
    originalProducts.value = original
    rawProducts.value = raw
    products.value = cartProductsList

    // Build the pre-lowercased search index when the cart is large enough
    // to make per-keystroke field-walking expensive. The single-cart flow
    // enters here (via fetchCartProducts) rather than through
    // initializeWithCartProducts, so the same index setup is needed.
    searchIndex = cartProductsList.length >= CART_SEARCH_INDEX_THRESHOLD
      ? buildCartSearchIndex(originalProducts.value)
      : null

    updateTotalPriceAndUnits()
    productsSliceY = computeSliceEnd(products.value.length, productsLimit)
    appendSlicePage(0, productsSliceY)
    productsSliceX = productsSliceY
  }

  function applyCartMetadata(resData) {
    if (Object.prototype.hasOwnProperty.call(resData, 'collectionEndDate')) {
      collectionEndDate.value = resData.collectionEndDate
    }
    if (Object.prototype.hasOwnProperty.call(resData, 'discountInCredit')) {
      discountInCredit.value = resData.discountInCredit
    }
    collectionName.value = resData?.collectionName
    collectionId.value = resData?.collectionId
  }

  function handleReplacingProduct() {
    const replacingProduct = root.$store.getters.getReplaceCarProduct
    if (!replacingProduct) return

    removeProductFromCart(replacingProduct)
    root.$store.commit(SET_REPLACE_CART_PRODUCT, null)
  }

  function trackBasketOpened(cartProductsList, resData) {
    if (!cartProductsList) return

    analytics.track(
      constants.TRACKS.PAGES.RETAILER_OPENS_BASKET,
      formatObject({
        collectionName: collectionName.value,
        noOfProductsInCart: resData?.products?.length || 0,
      }),
    )
  }

  function fetchCartProducts() {
    const resData = root.$store.getters.getCart
    emit('update-notes', resData?.notes || '')

    const cartProductsList = resData?.products || []
    const hasProducts = cartProductsList.length > 0

    if (hasProducts) {
      populateProductsFromCart(cartProductsList)
    }

    applyCartMetadata(resData)
    isLoaded.value = true
    emit('has-cart-product', hasProducts)
    handleReplacingProduct()
    trackBasketOpened(cartProductsList, resData)
  }

  // ---------------------------------------------------------------------------
  // saveCartProducts -- async/await, validation extracted
  // ---------------------------------------------------------------------------

  function buildSavePayload() {
    const payload = { products: [] }

    if (props.isPopup && props.retailerId) {
      payload.retailerId = props.retailerId
    }

    products.value.forEach(prod => {
      const originalProduct = rawProducts.value.find(p => p._id === prod._id)
      const entry = buildProductPayloadEntry(prod, originalProduct, isBrand)

      if (entry === 'USE_VARIANT_PROCESSOR') {
        processProductVariants(prod, originalProduct, payload, { isBrand })
      } else if (entry) {
        payload.products.push(entry)
      }
    })

    return payload
  }

  function handleSaveSuccess(res, payload, notesPayload, shouldSaveCart) {
    if (!props.isPopup && !shouldSaveCart) {
      apiToastSuccess(res.data.message || 'Saved cart!')
    }
    if (!isBrand.value) {
      analytics.track(
        constants.TRACKS.ACTIONS.RETAILER_SAVES_CART,
        formatObject({
          noOfProductsInCart: payload.products?.length,
          collectionName: collectionName.value,
        }),
      )
    }
    if (notesPayload?.notes) {
      addProductInCart(notesPayload)
    }
    rawProducts.value = JSON.parse(JSON.stringify(originalProducts.value))
  }

  function handleSaveError(err, shouldSaveCart) {
    if (err?.response?.data?.data?.hasRetailerCart) {
      isCartExport.value = shouldSaveCart
      root.$bvModal.show('clear-retailer-cart')
    }
  }

  function shouldSkipSave(shouldSaveCart) {
    return !shouldSaveCart && !isCartModified && !props.isPopup
  }

  function validateMinQuantities() {
    const errors = collectMinQtyValidationErrors(products.value)
    if (errors.length > 0) {
      errors.forEach(message => apiToastWarning(message))
      throw new Error('Minimum quantity validation failed')
    }
  }

  function hasProductsToSave(payload) {
    return Boolean(payload?.products?.length) || isBrand.value
  }

  async function saveCartProducts(notesPayload = {}, shouldSaveCart = false) {
    if (shouldSkipSave(shouldSaveCart)) return

    // Saving before an export must not be blocked by minimums, same as the
    // old cart page: the minimums are checked when the order is placed.
    const skipMinQtyValidation = shouldSaveCart
    if (!skipMinQtyValidation) {
      validateMinQuantities()
    }

    isCartModified = false
    const payload = buildSavePayload()
    const params = {
      isUpdateFromCartPage: true,
      ...(skipMinQtyValidation && { skipMinQtyValidation: true }),
    }

    if (!hasProductsToSave(payload)) return

    try {
      const res = await addProductInCart(payload, params)
      handleSaveSuccess(res, payload, notesPayload, shouldSaveCart)
    } catch (err) {
      handleSaveError(err, shouldSaveCart)
      throw new Error('Failed to save cart')
    }
  }

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------

  // Tests whether a single product matches the current search query. Used
  // from the template via `v-show` so search hides non-matching cards via
  // CSS instead of unmounting them — critical for performance: filtering a
  // 80+ product cart down to a few matches used to unmount dozens of
  // <b-table>/<b-form-input> components synchronously, which on iframe
  // pages triggered Chrome's "Page Unresponsive" dialog.
  function isProductMatch(product) {
    const query = searchText.value?.trim()?.toLowerCase() || ''
    if (!query) return true
    if (searchIndex) {
      const tokens = searchIndex[product?._id]
      return tokens ? tokens.indexOf(query) !== -1 : false
    }
    return searchProductUsingFields(
      product,
      CART_PRODUCTS_SEARCH_FIELDS,
      searchText.value,
      CART_PRODUCTS_SEARCH_ITEMS_FIELDS,
    )
  }

  // ---------------------------------------------------------------------------
  // initializeWithCartProducts
  // ---------------------------------------------------------------------------

  function initializeWithCartProducts(cartProductsList, isErrorProducts = false) {
    productsLimit = 20

    if (!cartProductsList?.length) {
      emit('has-cart-product', false)
      isLoaded.value = true
      return
    }

    const { original, raw } = seedAndSnapshotCartProducts(cartProductsList)
    originalProducts.value = original
    rawProducts.value = raw

    // Build the pre-lowercased search index only for carts large enough to
    // make per-keystroke field-walking expensive. Below the threshold the
    // existing matcher is plenty fast and the build cost isn't worth it.
    searchIndex = cartProductsList.length >= CART_SEARCH_INDEX_THRESHOLD
      ? buildCartSearchIndex(originalProducts.value)
      : null

    if (isErrorProducts) {
      products.value = getSortedCartProducts(cartProductsList)
      productsSlice.value = []
      productsSliceY = 0
    } else {
      products.value = cartProductsList
    }

    // Compute productsSliceY BEFORE updateTotalPriceAndUnits so it uses the
    // correct range.  In popup mode updateTotalPriceAndUnits empties and
    // refills productsSlice, so we must NOT push again afterwards.
    productsSliceY = computeSliceEnd(products.value.length, productsLimit)

    updateTotalPriceAndUnits()

    // In popup mode, updateTotalPriceAndUnits already empties and refills
    // productsSlice, so skip the extra push to avoid duplicates.
    if (!props.isPopup) {
      appendSlicePage(0, productsSliceY)
    }

    productsSliceX = productsSliceY

    emit('has-cart-product', true)
    isLoaded.value = true
  }

  // ---------------------------------------------------------------------------
  // Infinite scroll
  // ---------------------------------------------------------------------------

  function onScrollLoader(scrollContainer) {
    const hasMoreProducts = products.value.length > productsSliceX
    if (!isNearBottom(scrollContainer, 2000) || !hasMoreProducts) return

    pageNumber += 1
    productsSliceY = Math.min(products.value.length, pageNumber * productsLimit)
    appendSlicePage(productsSliceX, productsSliceY)
    productsSliceX = productsSliceY
  }

  function onScrollHandler() {
    onScrollLoader(getScrollContainer(props.modalId))
  }

  // ---------------------------------------------------------------------------
  // Event-driven setup
  // ---------------------------------------------------------------------------

  const handleReplaceProduct = product => {
    analytics.track(
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_REPLACE_PRODUCT_BUTTON,
      {
        cartId: root.$store.getters.getCart._id,
        retailerId: props.retailerId || root.$store.getters.getCart.userId || '',
        productId: product._id,
      },
    )
    onClickReplace(product)
  }

  const confirmProductRemoval = product => {
    trackRemoveProduct(product._id, 'confirm_modal_button_click')
    removeProduct(product)
    if (isBrand.value) {
      trackRemoveProduct(product._id, 'product_remove_success')
    }
  }

  watch(searchText, val => {
    const trackName = isBrand.value
      ? constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_SEARCHES_FOR_PRODUCT
      : constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SEARCHES_FOR_PRODUCT
    debouncedTrackSearchProductsInput(
      trackName, val, collectionId.value,
      props.retailerId, isBrand.value, root.$store.getters.getCart,
    )
  })

  onMounted(() => {
    getScrollContainer(props.modalId).addEventListener('scroll', onScrollHandler)
  })

  onUnmounted(() => {
    getScrollContainer(props.modalId).removeEventListener('scroll', onScrollHandler)
  })

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  if (props.cartProducts.length > 0) {
    initializeWithCartProducts(props.cartProducts)
  } else {
    fetchCartProducts()
  }

  return {
    products,
    productsSlice,
    isLoaded,
    variantLoaded,
    formattedDate,
    searchText,
    isProductMatch,
    originalProducts,
    collectionEndDate,
    discountInCredit,
    collectionId,
    isCartExport,
    collectionName,
    isBrand,
    trackErrorPopupClicks,
    trackRemoveProduct,
    handleReplaceProduct,
    confirmProductRemoval,
    onClickReplace,
    parseProductImage,
    updateProductVariants,
    saveCartProducts,
    fetchCartProducts,
    removeMultipleProducts,
    formatNumber,
    formatCurrency,
    updateQuantity,
    initializeWithCartProducts,
    // utils
    imageLoadError,
    getMaxQuantity,
    isProductValueDisabled,
    hasProductMaxQuantity,
    isNotValidMinQty,
    minQtyErrorMessage,
    getCartProductsErrorStatusText,
    isProductHasZeroQty,
    isProductWithDecimalQty,
  }
}
